/**
 * The 400 m oval. Two 115 m straights joined by semicircular bends; the kerb (inside of
 * lane 1) measures exactly 400 m. The home straight runs along +x with the infield on the
 * runners' left (-z), so laps go anticlockwise like a real track. The finish line is at the
 * end of the home straight (x = FINISH_X); the 100 m starts 100 m before it.
 */
export const LANE_WIDTH = 1.22;
export const LANES = 8;
export const TRACK_WIDTH = LANE_WIDTH * LANES;
export const STRAIGHT = 115;
export const FINISH_X = 100;
export const HOME_START_X = FINISH_X - STRAIGHT;
/** Kerb radius so that 2 straights + 2 half-circles = 400 m. */
export const KERB_RADIUS = (400 - 2 * STRAIGHT) / (2 * Math.PI);

/** Lateral offset of a lane's centre line from the kerb (m). */
export function laneOffset(lane: number): number {
  return (lane + 0.5) * LANE_WIDTH;
}

/** Lap length along a lane's centre line. */
export function lapLength(lane: number): number {
  return 2 * STRAIGHT + 2 * Math.PI * (KERB_RADIUS + laneOffset(lane));
}

export interface TrackPose {
  x: number;
  z: number;
  /** Unit direction of travel. */
  dx: number;
  dz: number;
}

/**
 * Position `u` metres along a lane's centre line, measured from the finish line going the
 * way of the race: far bend, back straight, near bend, home straight.
 */
export function trackPose(u: number, lane: number): TrackPose {
  const off = laneOffset(lane);
  const r = KERB_RADIUS + off;
  const lap = lapLength(lane);
  let s = u % lap;
  if (s < 0) s += lap;
  const bend = Math.PI * r;
  if (s < bend) {
    // Far bend, centred at (FINISH_X, -KERB_RADIUS), turning left from +x.
    const a = s / r;
    return {
      x: FINISH_X + r * Math.sin(a),
      z: -KERB_RADIUS + r * Math.cos(a),
      dx: Math.cos(a),
      dz: -Math.sin(a),
    };
  }
  s -= bend;
  if (s < STRAIGHT) {
    // Back straight, running -x.
    return { x: FINISH_X - s, z: -2 * KERB_RADIUS - off, dx: -1, dz: 0 };
  }
  s -= STRAIGHT;
  if (s < bend) {
    // Near bend, centred at (HOME_START_X, -KERB_RADIUS).
    const a = Math.PI + s / r;
    return {
      x: HOME_START_X + r * Math.sin(a),
      z: -KERB_RADIUS + r * Math.cos(a),
      dx: Math.cos(a),
      dz: -Math.sin(a),
    };
  }
  s -= bend;
  // Home straight, running +x to the finish.
  return { x: HOME_START_X + s, z: off, dx: 1, dz: 0 };
}

/** Where a race of `distance` metres starts in this lane so that everyone finishes at the line. */
export function raceStart(lane: number, distance: number): number {
  const lap = lapLength(lane);
  const laps = Math.ceil(distance / lap - 1e-9);
  return laps * lap - distance;
}

/**
 * Races longer than one lap are not run in lanes: a staggered start would put the outer
 * lanes a third of a lap ahead once each lane is forced to whole laps. Everyone runs the
 * inside with a small lateral offset per athlete (a waterfall start), so the field stays
 * together and visible.
 */
export function raceLane(lane: number, distance: number): number {
  return distance > 400 ? lane * 0.3 : lane;
}

/** Pose of a runner `d` metres into a race of `distance` metres in `lane`. */
export function racePose(d: number, lane: number, distance: number): TrackPose {
  const l = raceLane(lane, distance);
  return trackPose(raceStart(l, distance) + d, l);
}
